import {IncomingMessage} from 'http'
import {IBizType} from '@/plugins/initOpenAppSDK'
import shuffleArrayWithSalt from '@/utils/shuffleArrayWithSalt'
import {IGradeInfo, ISubjectInfo} from './common/requestBookInfo'
import request from '.'
import {ReciteType} from './types/enum'

/**
 * @description: 引导状态
 * @enum: noGuide 0 不展示引导
 * @enum: default 1 展示引导(有频率限制)
 * @enum: canNotClose 2 引导不可以关闭
 */
export enum GUIDE_STATUS {
    noGuide,
    default,
    canNotClose,
}

/**
 * @description: 二维码对应的页面类型
 * @enum: video 1 视频
 * @enum: audio 2 音频
 * @enum: PDF 3 PDF
 * @enum: speech 10 语音测评
 * @enum: listening 11 听写
 * @enum: writing 12 作文
 * @enum: LLRM 13 听学读背
 * @enum: mentalArithmetic 14 口算
 */
export enum RESOURCE_TYPE {
    video = 1,
    audio,
    PDF,
    speech = 10,
    listening,
    writing,
    LLRM,
    mentalArithmetic
}
export const RESOURCE_TYPE_CN = {
    [RESOURCE_TYPE.video]: '视频',
    [RESOURCE_TYPE.audio]: '音频',
    [RESOURCE_TYPE.PDF]: 'PDF',
    [RESOURCE_TYPE.speech]: '语音测评',
    [RESOURCE_TYPE.listening]: '听写题',
    [RESOURCE_TYPE.writing]: '作文',
    [RESOURCE_TYPE.LLRM]: '听学读背',
    [RESOURCE_TYPE.mentalArithmetic]: '口算',
}
export enum RESOURCE_TYPE_REPORT {
    video = 1,
    audio,
    PDF,
    talk = 10,
    wirte,
    writing,
    text,
    correct
}

export interface IGuideInfo {
    guideStatus: GUIDE_STATUS;
    homeGuideStatus: GUIDE_STATUS;
    midGuideStatus: GUIDE_STATUS;
    midGuideImgUrl: string;
    midGuideJumpUrl: string;
    qrcodeStatus: GUIDE_STATUS;
    qrcodeImgUrl: string;
    friendView?: {
        appId: string;
        corpId: string;
        weComId: string;
        token: string;
    }
}

export interface IMedia {
    qrcodeId: string;
    flatIndex?: number;
    progressId?: string;
    coverUrl: string;
    materialName: string;
    timeLength: number;
    videoPlayedTime?: number;
    maxPlayedTime?: number;
    materialUrl: string;
    lastRead?: boolean;
    materialId: string;
    materialType: RESOURCE_TYPE;
}

export interface IListeningItem {
    questionId: SN;
    audioUrl: string;
    name: string;
    answer: string;
    hasListened: boolean;
    lastListened: boolean;
}

export interface ILLRMModule {
    percent?: number;
    star?: number;
    score?: number;
    questionId?:SN;
}

export interface IMediaListRes {
    bookId: SN;
    bookName: string;
    chapterId: SN;
    chapterName: string;
    gradeList: IGradeInfo[];
    subjectInfo: ISubjectInfo;
    bookPClient: IBizType;
    qrcodeId: SN;
    qrcodeName: string;
    list: IMedia[];
    textList: string[];
    resourceType: RESOURCE_TYPE;
    guideInfo: IGuideInfo;
    coverUrl: string;
    dictationQuestion?: {
        questionId: SN;
        name: string;
        childrenQuestionList: IListeningItem[];
        readCount: number;
        readInterval: number;
        readConfig: {
            readCountList: number[];
            readIntervalList: number[];
        };
    } | null;
    lessonTextLearnMode?: {
        questionId: SN;
        lessonTextDetail?: ILessonTextDetail;
        listenModeProgress?: number;
        learnModeProgress?: number;
        readMode?: ILLRMModule;
        reciteMode?: ILLRMModule;
        reciteConfig: IReciteConfig;
    } | null;
}

export interface IIMediaListResEx extends IMediaListRes {
    dictationQuestion?: (IMediaListRes['dictationQuestion'] & {
        shuffledList: IListeningItem[];
        lastListeningIndex: number;
    }) | null;
    lessonTextLearnMode?: (IMediaListRes['lessonTextLearnMode'] & {
        contentList: string[];
    }) | null;
}

interface IMediaListProps {
    qrcodeId: string;
    salt?: string;
}

export const requestMediaList = ({qrcodeId, salt}: IMediaListProps, ctx?: CTX) => request<IMediaListRes>({
    url: '/books/material/h5/detailList',
    method: 'post',
    data: {qrcodeId},
    ctx,
}).then((res) => {
    const {success, data} = res
    if (!success || !data) {
        return {
            ...res,
            data: null,
            error: res.data,
            success: false,
        } as IError
    }
    const {
        resourceType,
        list,
        dictationQuestion,
        lessonTextLearnMode,
    } = data
    const {childrenQuestionList = []} = dictationQuestion || {}
    const shuffledList = [...childrenQuestionList]
    shuffleArrayWithSalt(shuffledList, salt)
    const lastListeningIndex = Math.max(childrenQuestionList.findIndex((item) => item.lastListened), 0)
    let contentList: string[] = []
    if (resourceType === RESOURCE_TYPE.video) {
        data.list = list.filter((item) => item.materialType === RESOURCE_TYPE.video)
    }
    else if (resourceType === RESOURCE_TYPE.audio) {
        data.list = list.filter((item) => item.materialType === RESOURCE_TYPE.audio)
    }
    else if (resourceType === RESOURCE_TYPE.PDF) {
        data.list = list.filter((item) => item.materialType === RESOURCE_TYPE.PDF)
    }
    else if (resourceType === RESOURCE_TYPE.LLRM) {
        contentList = lessonTextLearnMode!.lessonTextDetail?.paragraphList.map(
            (item) => item.contentList.map((item) => item.text).join('')
        ) || []
    }
    const dataEx: IIMediaListResEx = {
        ...data,
        dictationQuestion: dictationQuestion
            ? {...dictationQuestion, shuffledList, lastListeningIndex}
            : null,
        lessonTextLearnMode: lessonTextLearnMode
            ? {...lessonTextLearnMode, contentList}
            : null,
    }
    res.data = dataEx
    return {...res, data: dataEx} as ISuccess<IIMediaListResEx>
})

export const requestMediaProgress = (data: {
    progressId?: string;
    mediaProgress: number;
}) => {
    if (!data.progressId) {
        return
    }

    return request({url: '/books/media/progress', method: 'post', data})
}

export const requestPlayUrl = (materialId: string) => request<string>({
    url: '/material/h5/getMediaPlayUrl',
    method: 'post',
    data: {materialId},
})

/**
 * 检查资源是否需要登录。
 * @param params.gid  - habo id 用于判断用户是否首次进入页面且是否进行登录，后续会根据sid和userId进行关联
 *              存在数据以及判断不准确问题
 * @param params.bookId - 书籍id
 * @returns Promise。
 */
export const materialNeedLogin = (data:IObject) => request<boolean>({url: '/books/needLogin', method: 'post', data})

/**
 * 上传登录信息。
 * @param data.gid 哈勃id
 * @param data.bookId - 书籍id
 * @param data.qrcodeUrl - 地址
 * @returns  Promise。
 */
export interface MaterialReportLoginParams {
  gid?: string;
  bookId ?: SN;
  qrcodeUrl ?: string;
}
export const materialReportLogin = (data: MaterialReportLoginParams) => request({url: '/books/reportLogin', method: 'post', data})